import { c as _c } from "react/compiler-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo } from 'react';
import { sdkEvents } from '../core/events.js';
import { installMarketConfigRefresh, marketConfigCatalog, requestMarketConfig } from '../core/internal/market-config-catalog.js';
import { useGetMarketConfigs } from '../generated/hooks/useGetMarketConfigs.gen.js';
import { PersistenceRestoredContext } from './PersistenceRestoredContext.js';
const CatalogContext = createContext(null);
/** The only catalog QueryObserver in an SDK/network tree. Leaves read this context. */
export function MarketConfigProvider(t0) {
  const $ = _c(32);
  const {
    children
  } = t0;
  const restored = useContext(PersistenceRestoredContext);
  const client = useQueryClient();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== restored) {
    t2 = {
      enabled: restored,
      retry: false
    };
    $[1] = restored;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const result = useGetMarketConfigs(t1, t2);
  const refetch = result.refetch;
  let t3;
  let t4;
  if ($[3] !== client || $[4] !== refetch || $[5] !== restored) {
    t3 = () => {
      if (!restored) {
        return;
      }
      return installMarketConfigRefresh(client, async () => {
        const refreshed = await refetch({
          cancelRefetch: false
        });
        if (refreshed.isError) {
          sdkEvents.emit("cache:diagnostic", {
            kind: "market-config",
            reason: "refresh-failed"
          });
          throw refreshed.error ?? new Error("market config refresh failed");
        }
      });
    };
    t4 = [client, refetch, restored];
    $[3] = client;
    $[4] = refetch;
    $[5] = restored;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useLayoutEffect(t3, t4);
  let t5;
  if ($[8] !== client) {
    t5 = t6 => {
      const address = t6 === undefined ? "*" : t6;
      return requestMarketConfig(client, address, true);
    };
    $[8] = client;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const retry = t5;
  let t6;
  if ($[10] !== client || $[11] !== restored) {
    t6 = address_0 => {
      if (restored) {
        requestMarketConfig(client, address_0);
      }
    };
    $[10] = client;
    $[11] = restored;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const ensure = t6;
  let t7;
  if ($[13] !== refetch || $[14] !== restored) {
    t7 = async () => {
      if (restored) {
        await refetch({
          cancelRefetch: false
        });
      }
    };
    $[13] = refetch;
    $[14] = restored;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const refresh = t7;
  let t8;
  if ($[16] !== result.data) {
    t8 = marketConfigCatalog(result.data);
    $[16] = result.data;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const configs = t8;
  const status = !restored ? "loading" : configs !== undefined ? "ready" : result.isError || result.data !== undefined ? "unavailable" : "loading";
  const {
    error,
    isFetching,
    dataUpdatedAt,
    errorUpdatedAt,
    failureCount
  } = result;
  let t9;
  if ($[18] !== configs || $[19] !== dataUpdatedAt || $[20] !== ensure || $[21] !== error || $[22] !== errorUpdatedAt || $[23] !== failureCount || $[24] !== isFetching || $[25] !== refresh || $[26] !== retry || $[27] !== status) {
    t9 = {
      configs,
      status,
      retry,
      ensure,
      refetch: refresh,
      error,
      isFetching,
      dataUpdatedAt,
      errorUpdatedAt,
      failureCount
    };
    $[18] = configs;
    $[19] = dataUpdatedAt;
    $[20] = ensure;
    $[21] = error;
    $[22] = errorUpdatedAt;
    $[23] = failureCount;
    $[24] = isFetching;
    $[25] = refresh;
    $[26] = retry;
    $[27] = status;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  const value = t9;
  let t10;
  if ($[29] !== children || $[30] !== value) {
    t10 = _jsx(CatalogContext.Provider, {
      value,
      children
    });
    $[29] = children;
    $[30] = value;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  return t10;
}
export function useMarketConfigCatalog() {
  const value = useContext(CatalogContext);
  if (value === null) {
    throw new Error("MarketConfigProvider is required");
  }
  return value;
}
export function useMarketConfig(address) {
  const $ = _c(6);
  const {
    configs,
    status
  } = useMarketConfigCatalog();
  const client = useQueryClient();
  const config = address == null ? undefined : configs?.[address];
  let t0;
  let t1;
  if ($[0] !== address || $[1] !== client || $[2] !== config || $[3] !== status) {
    t0 = () => {
      if (status === "ready" && config === undefined && address != null) {
        requestMarketConfig(client, address);
      }
    };
    t1 = [address, client, config, status];
    $[0] = address;
    $[1] = client;
    $[2] = config;
    $[3] = status;
    $[4] = t0;
    $[5] = t1;
  } else {
    t0 = $[4];
    t1 = $[5];
  }
  useEffect(t0, t1);
  return config;
}